import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Namespace } from 'enums-lib';
import { ENVIRONMENT } from 'environments-lib';
import { IEnvironment } from 'interfaces-lib';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { SharedServiceProviderServiceFactory } from 'shared-service-provider-lib';
import { IdentityStorage } from 'storage-service-lib';

export interface IClientConfig {
  enabled: boolean;
  clientId: string;
  clientSecret: string;
  aiAssistEnabled: boolean;
  docMergeEnabled: boolean;
}

@Injectable({ providedIn: 'root' })
export class ClientConfigService {
  private clientConfigCache = new BehaviorSubject<IClientConfig | null>(null);
  private clientConfigCache$ = this.clientConfigCache.asObservable();
  private configLoaded = false;

  constructor(
    @Inject(ENVIRONMENT) private environment: Partial<IEnvironment>,
    private http: HttpClient,
    private identityStorage: IdentityStorage,
    private serviceProviderFactory: SharedServiceProviderServiceFactory,
  ) {
    const identity = this.identityStorage.get();
    if (identity) {
      this.loadConfig();
    }
  }

  private loadConfig(): void {
    if (this.configLoaded) {
      return;
    }
    try {
      this.getClientConfig().subscribe({
        next: (config) => {
          this.clientConfigCache.next(config);
          this.configLoaded = true;
        },
        error: (err) => {
          console.error('Failed to load client config', err);
          this.clientConfigCache.next({
            enabled: false,
            clientId: '',
            clientSecret: '',
            aiAssistEnabled: false,
            docMergeEnabled: false,
          });
        },
      });
    } catch (error) {
      console.error('Failed to load client config', error);
      this.clientConfigCache.next({
        enabled: false,
        clientId: '',
        clientSecret: '',
        aiAssistEnabled: false,
        docMergeEnabled: false,
      });
    }
  }

  isAiAssistEnabled(): Observable<boolean> {
    this.loadConfig();
    return this.clientConfigCache$.pipe(
      filter((response) => response !== null),
      map((response) => response?.aiAssistEnabled ?? false),
    );
  }

  isDocMergeEnabled(): Observable<boolean> {
    this.loadConfig();
    return this.clientConfigCache$.pipe(
      filter((response) => response !== null),
      map((response) => {
        return response?.docMergeEnabled ?? false;
      })
    );
  }

  private getClientConfig(): Observable<IClientConfig> {
    const identity = this.identityStorage.get();
    if (!identity) {
      throw new Error('Identity not found');
    }

    const namespace = identity.namespace;
    const corpId = this.getCorpId();
    const url = `${this.environment.adApiGatewayUrl}/api/adv2/client/${namespace}/${corpId}`;

    const apiKey = this.environment.adApiGatewayKey;
    if (!apiKey) {
      throw new Error('API Gateway key not configured');
    }

    return this.http.get<IClientConfig>(url, {
      headers: {
        'x-api-key': apiKey,
      },
    });
  }

  private getCorpId(): string {
    const identity = this.identityStorage.get();
    if (!identity) {
      throw new Error('Identity not found');
    }
    if (!identity.token) {
      throw new Error('Token not found in identity');
    }
    return this.serviceProviderFactory
      .getServiceProviderService(identity.namespace)
      .getCorpId(identity.token);
  }
}
